import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { Mail, CheckCircle2, RotateCcw, RefreshCw } from "lucide-react";

/**
 * Admin panel — list & mark-resolved every contact-form submission.
 * Backed by /api/admin/enquiries.
 */
const STATUS_TABS = [
  { key: "open", label: "Open" },
  { key: "resolved", label: "Resolved" },
  { key: "all", label: "All" },
];

const EnquiriesPanel = () => {
  const [status, setStatus] = useState("open");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/enquiries?status=${status}`);
      setItems(r.data.items || []);
    } catch { /* silent */ }
    setLoading(false);
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (id, next) => {
    setBusyId(id);
    try {
      await api.patch(`/admin/enquiries/${id}?status=${next}`);
      await load();
    } catch { /* silent */ }
    setBusyId(null);
  };

  return (
    <div data-testid="admin-enquiries-card" className="bg-white rounded-2xl soft-shadow p-6 space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Mail className="w-5 h-5 text-coral-400" />
          <h2 className="font-semibold text-navy-700 text-lg">Contact enquiries</h2>
        </div>
        <button
          onClick={load}
          data-testid="admin-enquiries-refresh"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-navy-700/60 hover:text-navy-700"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      <div className="flex gap-2 flex-wrap">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setStatus(t.key)}
            data-testid={`enquiries-tab-${t.key}`}
            className={`text-xs px-3 py-1.5 rounded-full font-semibold border ${
              status === t.key
                ? "bg-coral-300 text-white border-coral-300"
                : "bg-white text-navy-700/70 border-cream-300 hover:bg-cream-100"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-sm text-navy-700/50">Loading…</p>
      ) : items.length === 0 ? (
        <p data-testid="admin-enquiries-empty" className="text-sm text-navy-700/50">
          No enquiries in this view.
        </p>
      ) : (
        <div data-testid="admin-enquiries-list" className="divide-y divide-cream-200">
          {items.map((it) => (
            <div
              key={it.id}
              data-testid={`enquiry-row-${it.id}`}
              className="py-4 space-y-2"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-navy-700">
                    {it.name}{" "}
                    <a
                      href={`mailto:${it.email}?subject=Re: ${encodeURIComponent(it.subject_label)}`}
                      className="text-coral-500 hover:underline text-sm"
                    >
                      &lt;{it.email}&gt;
                    </a>
                  </div>
                  <div className="text-xs text-navy-700/50">
                    {it.subject_label} · {new Date(it.created_at).toLocaleString()}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span
                    className={`text-[10px] uppercase tracking-wider rounded px-2 py-1 font-bold ${
                      it.status === "open"
                        ? "bg-amber-50 text-amber-700"
                        : "bg-green-50 text-green-700"
                    }`}
                  >
                    {it.status}
                  </span>
                  <button
                    onClick={() => toggle(it.id, it.status === "open" ? "resolved" : "open")}
                    disabled={busyId === it.id}
                    data-testid={`enquiry-toggle-${it.id}`}
                    className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full disabled:opacity-60 ${
                      it.status === "open"
                        ? "bg-green-600 text-white hover:bg-green-700"
                        : "border border-navy-700/20 text-navy-700 hover:bg-cream-100"
                    }`}
                  >
                    {it.status === "open"
                      ? <><CheckCircle2 className="w-3.5 h-3.5" /> Resolve</>
                      : <><RotateCcw className="w-3.5 h-3.5" /> Re-open</>}
                  </button>
                </div>
              </div>
              <div className="text-sm text-navy-700/80 whitespace-pre-wrap bg-cream-100/60 rounded-md p-3">
                {it.message}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EnquiriesPanel;
